import { TablePagination } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { PAGINATION_PAGE_SIZE_OPTIONS } from '../../constants/config';

/** Toolbar min-height (px). */
const PAGINATION_TOOLBAR_MIN_HEIGHT = 48;
/** Vertical padding above/below the toolbar (theme spacing units = 4px each). */
const PAGINATION_TOOLBAR_PY = 0.5;
/**
 * Total rendered height of the pager bar (px): toolbar + top/bottom padding.
 * Pages that cap a viewport-fit DataTable pass this as `bottomReserve` so the
 * pager stays on screen below the table (BUG-2026-0206).
 */
export const PAGINATION_BAR_HEIGHT = PAGINATION_TOOLBAR_MIN_HEIGHT + 8;

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  entityLabel?: string;
  /** Override the page-size choices. Use `{ label: 'All', value: -1 }` for a
   *  "show everything" option. Defaults to the app-wide PAGINATION options. */
  pageSizeOptions?: (number | { label: string; value: number })[];
}

export const Pagination = ({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  entityLabel = 'projects',
  pageSizeOptions,
}: PaginationProps) => {
  const theme = useTheme();

  return (
    <TablePagination
      component="div"
      count={total}
      page={page}
      rowsPerPage={pageSize}
      rowsPerPageOptions={pageSizeOptions ?? PAGINATION_PAGE_SIZE_OPTIONS}
      onPageChange={(_event, newPage) => onPageChange(newPage)}
      onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
      labelDisplayedRows={({ from, to, count }) =>
        `${from}-${to} of ${count} ${entityLabel}`
      }
      sx={{
        '.MuiTablePagination-toolbar': {
          justifyContent: 'flex-end',
          minHeight: `${PAGINATION_TOOLBAR_MIN_HEIGHT}px !important`,
          py: PAGINATION_TOOLBAR_PY,
        },
        '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
          fontSize: '0.8rem',
          color: alpha(theme.palette.text.primary, 0.6),
          fontWeight: 500,
        },
        '.MuiTablePagination-actions .MuiIconButton-root': {
          borderRadius: '8px',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
          },
        },
        borderTop: 0,
      }}
    />
  );
};
